import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import { useContext, useState } from 'react';
import { useToast } from '../../../hooks/useToast/useToast';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { ImageEditButton } from './_components/ImageEditButton';
import { LinkButton } from './_components/LinkButton';
import { PreviewButton } from './_components/PreviewButton';
import { YouTubeButton } from './_components/YouTubeButton';
import { TEXT_ALIGN } from './_constants';
import type { CustomImageOptions } from './_utils/CustomImage';
import type { CustomLinkOptions } from './_utils/CustomLink';
import { extractAlignValue } from './_utils/extractAlignValue';
import { extractMaxWidthValue } from './_utils/extractMaxWidthValue';
import type { BizFontSizeType } from './_utils/FontSize';
import { BIZ_FONT_SIZES, DEFAULT_FONT_SIZE } from './_utils/FontSize';
import { FONT_WEIGHTS } from './_utils/FontWeight';
import { getYouTubeVideoId } from './_utils/getYouTubeVideoId';
import { HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLORS } from './_utils/TextColor';
export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
}
const DEFAULT_CUSTOM_IMAGE_OPTIONS = {
  maxWidth: null,
  isBorderRadius: false,
  align: 'left'
} as const;
const DEFAULT_CUSTOM_LINK_OPTIONS = {
  theme: 'text',
  size: 'H56',
  isOutLink: false,
  href: ''
} as const;
export const BizToolbar = t0 => {
  const $ = _c(106);
  const {
    imageUploadOptions
  } = t0;
  const {
    toast,
    toastError
  } = useToast();
  const {
    editor
  } = useContext(CustomEditorContext);
  const isImageSelected = editor?.state.selection instanceof NodeSelection && editor?.state.selection.node.type.name === "image";
  const [customImageOptions, setCustomImageOptions] = useState(DEFAULT_CUSTOM_IMAGE_OPTIONS);
  const [customLinkOptions, setCustomLinkOptions] = useState(DEFAULT_CUSTOM_LINK_OPTIONS);
  const [youTubeUrl, setYouTubeUrl] = useState("");
  let t1;
  if ($[0] !== editor) {
    const getSelectedNodeAttrText = attrName => {
      if (!editor) {
        return "";
      }
      const selection = editor.state.selection;
      if (!(selection instanceof NodeSelection)) {
        return "";
      }
      const attrs = selection.node.attrs as Record<string, unknown>;
      const value = attrs[attrName];
      return typeof value === "string" ? value : "";
    };
    t1 = () => {
      const styleText = getSelectedNodeAttrText("style");
      const classText = getSelectedNodeAttrText("class");
      const maxWidth = extractMaxWidthValue(styleText);
      const isBorderRadius = classText.includes("rounded");
      const align = extractAlignValue(classText);
      setCustomImageOptions({
        maxWidth,
        isBorderRadius,
        align
      });
    };
    $[0] = editor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialImageOptions = t1;
  let t2;
  if ($[2] !== isImageSelected) {
    t2 = () => {
      if (isImageSelected) {
        setCustomLinkOptions(_temp);
      }
    };
    $[2] = isImageSelected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const initialLinkOptions = t2;
  if (!editor) {
    return null;
  }
  let t3;
  if ($[4] !== editor) {
    t3 = TEXT_COLORS.find(t4 => {
      const [, color] = t4;
      return editor.isActive("textStyle", {
        color
      });
    }) ?? [];
    $[4] = editor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [selectedColor] = t3;
  let t4;
  if ($[6] !== editor) {
    t4 = FONT_WEIGHTS.find(fontWeight => editor.isActive("fontWeight", {
      fontWeight
    }));
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selectedFontWeight = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = BIZ_FONT_SIZES.find(fontSize => editor.isActive("fontSize", {
      fontSize
    })) ?? DEFAULT_FONT_SIZE;
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedFontSize = t5;
  let isBoxedText;
  let t10;
  let t11;
  let t12;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[10] !== editor || $[11] !== selectedColor || $[12] !== selectedFontSize || $[13] !== selectedFontWeight) {
    isBoxedText = editor.isActive("boxedText");
    let t13;
    if ($[22] !== editor) {
      t13 = TEXT_ALIGN.find(textAlign => editor.isActive("textAlign", {
        textAlign
      }));
      $[22] = editor;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    const selectedTextAlign = t13;
    t7 = "flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8";
    let t14;
    if ($[24] !== editor) {
      t14 = e => {
        editor.chain().focus().setBizFontSize(e.target.value as BizFontSizeType).run();
      };
      $[24] = editor;
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    let t15;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = BIZ_FONT_SIZES.map(_temp2);
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    if ($[27] !== selectedFontSize || $[28] !== t14) {
      t8 = <select value={selectedFontSize} onChange={t14}>{t15}</select>;
      $[27] = selectedFontSize;
      $[28] = t14;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    let t16;
    if ($[30] !== editor) {
      t16 = e_0 => {
        editor?.chain().focus().setTextAlign(e_0.target.value).run();
      };
      $[30] = editor;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = TEXT_ALIGN.map(_temp3);
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    if ($[33] !== selectedTextAlign || $[34] !== t16) {
      t9 = <select value={selectedTextAlign} onChange={t16}>{t17}</select>;
      $[33] = selectedTextAlign;
      $[34] = t16;
      $[35] = t9;
    } else {
      t9 = $[35];
    }
    let t18;
    if ($[36] !== editor || $[37] !== selectedColor) {
      t18 = TEXT_COLORS.map(t19 => {
        const [label, color_0] = t19;
        return <button key={label} className={cn("font-800", {
          underline: selectedColor === label
        })} style={{
          color: color_0
        }} type="button" onClick={() => editor?.chain().focus().setColor(color_0).run()}>A</button>;
      });
      $[36] = editor;
      $[37] = selectedColor;
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    if ($[39] !== t18) {
      t10 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t18}</section>;
      $[39] = t18;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    let t19;
    if ($[41] !== editor || $[42] !== selectedFontWeight) {
      t19 = FONT_WEIGHTS.map(fontWeight_0 => <button key={fontWeight_0} className={cn(fontWeight_0, {
        underline: selectedFontWeight === fontWeight_0
      })} type="button" onClick={() => editor?.chain().focus().setFontWeight(fontWeight_0).run()}>B</button>);
      $[41] = editor;
      $[42] = selectedFontWeight;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    if ($[44] !== t19) {
      t11 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t19}</section>;
      $[44] = t19;
      $[45] = t11;
    } else {
      t11 = $[45];
    }
    if ($[46] !== editor) {
      t12 = HIGHLIGHTS.map(highlight => {
        const isActive = editor.isActive("highlight", {
          highlight
        });
        return <button key={highlight} className={cn(highlight, "flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700", {
          "border-1 border-Gray-800": isActive
        })} type="button" onClick={() => isActive ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().setHighlight(highlight).run()} />;
      });
      $[46] = editor;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    t6 = cn("box-border flex size-24 items-center justify-center", {
      "border-Gray-800 border-1 rounded-xxsmall": isBoxedText
    });
    $[10] = editor;
    $[11] = selectedColor;
    $[12] = selectedFontSize;
    $[13] = selectedFontWeight;
    $[14] = isBoxedText;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    isBoxedText = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t13;
  if ($[48] !== editor || $[49] !== isBoxedText) {
    t13 = () => isBoxedText ? editor.chain().focus().unsetBoxedText().run() : editor.chain().focus().setBoxedText().run();
    $[48] = editor;
    $[49] = isBoxedText;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BoxedTextIcon />;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t13 || $[53] !== t6) {
    t15 = <button className={t6} type="button" onClick={t13}>{t14}</button>;
    $[52] = t13;
    $[53] = t6;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== editor) {
    t16 = () => {
      editor?.chain().focus().setHorizontalRule().run();
    };
    $[55] = editor;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <HrIcon />;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t16) {
    t18 = <button type="button" onClick={t16}>{t17}</button>;
    $[58] = t16;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== imageUploadOptions) {
    t19 = <ImageButton {...imageUploadOptions} />;
    $[60] = imageUploadOptions;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const t20 = !isImageSelected;
  let t21;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = align_0 => {
      setCustomImageOptions(prev_0 => ({
        ...prev_0,
        align: align_0
      }));
    };
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = isBorderRadius_0 => {
      setCustomImageOptions(prev_1 => ({
        ...prev_1,
        isBorderRadius: isBorderRadius_0
      }));
    };
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== customImageOptions || $[65] !== editor) {
    t23 = () => {
      editor.chain().focus().setCustomImage(customImageOptions).run();
      setCustomImageOptions(DEFAULT_CUSTOM_IMAGE_OPTIONS);
    };
    $[64] = customImageOptions;
    $[65] = editor;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = maxWidth_0 => {
      setCustomImageOptions(prev_2 => ({
        ...prev_2,
        maxWidth: maxWidth_0
      }));
    };
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== customImageOptions || $[69] !== initialImageOptions || $[70] !== t20 || $[71] !== t23) {
    t25 = <ImageEditButton disabled={t20} selectedOptions={customImageOptions} onAlignChange={t21} onClick={initialImageOptions} onIsBorderRadiusChange={t22} onSubmit={t23} onWidthChange={t24} />;
    $[68] = customImageOptions;
    $[69] = initialImageOptions;
    $[70] = t20;
    $[71] = t23;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== editor || $[74] !== toast || $[75] !== toastError || $[76] !== youTubeUrl) {
    t26 = () => {
      if (youTubeUrl) {
        ;
        try {
          const videoId = getYouTubeVideoId(youTubeUrl);
          editor.chain().focus().setCustomYouTubeIframe({
            src: `https://www.youtube.com/embed/${videoId}`
          }).run();
          setYouTubeUrl("");
        } catch (t27) {
          const error = t27;
          if (error instanceof Error && error.message) {
            toast({
              type: "fail",
              message: error.message
            });
            return;
          }
          toastError({
            error
          });
        }
      }
    };
    $[73] = editor;
    $[74] = toast;
    $[75] = toastError;
    $[76] = youTubeUrl;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== t26 || $[79] !== youTubeUrl) {
    t27 = <YouTubeButton youTubeUrl={youTubeUrl} onSubmit={t26} onYouTubeUrlChange={setYouTubeUrl} />;
    $[78] = t26;
    $[79] = youTubeUrl;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  let t29;
  let t30;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = href => {
      setCustomLinkOptions(prev_3 => ({
        ...prev_3,
        href
      }));
    };
    t29 = isOutLink => {
      setCustomLinkOptions(prev_4 => ({
        ...prev_4,
        isOutLink
      }));
    };
    t30 = size => {
      setCustomLinkOptions(prev_5 => ({
        ...prev_5,
        size
      }));
    };
    $[81] = t28;
    $[82] = t29;
    $[83] = t30;
  } else {
    t28 = $[81];
    t29 = $[82];
    t30 = $[83];
  }
  let t31;
  if ($[84] !== customLinkOptions || $[85] !== editor) {
    t31 = () => {
      editor.chain().focus().setCustomLink(customLinkOptions).run();
      setCustomLinkOptions(DEFAULT_CUSTOM_LINK_OPTIONS);
    };
    $[84] = customLinkOptions;
    $[85] = editor;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = theme => {
      setCustomLinkOptions(prev_6 => ({
        ...prev_6,
        theme
      }));
    };
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  let t33;
  if ($[88] !== customLinkOptions || $[89] !== initialLinkOptions || $[90] !== t31) {
    t33 = <LinkButton selectedOptions={customLinkOptions} onClick={initialLinkOptions} onHrefChange={t28} onIsOutLinkChange={t29} onSizeChange={t30} onSubmit={t31} onThemeChange={t32} />;
    $[88] = customLinkOptions;
    $[89] = initialLinkOptions;
    $[90] = t31;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <PreviewButton />;
    $[92] = t34;
  } else {
    t34 = $[92];
  }
  let t35;
  if ($[93] !== t10 || $[94] !== t11 || $[95] !== t12 || $[96] !== t15 || $[97] !== t18 || $[98] !== t19 || $[99] !== t25 || $[100] !== t27 || $[101] !== t33 || $[102] !== t7 || $[103] !== t8 || $[104] !== t9) {
    t35 = <nav className={t7}>{t8}{t9}{t10}{t11}{t12}{t15}{t18}{t19}{t25}{t27}{t33}{t34}</nav>;
    $[93] = t10;
    $[94] = t11;
    $[95] = t12;
    $[96] = t15;
    $[97] = t18;
    $[98] = t19;
    $[99] = t25;
    $[100] = t27;
    $[101] = t33;
    $[102] = t7;
    $[103] = t8;
    $[104] = t9;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  return t35;
};
function _temp(prev) {
  return {
    ...prev,
    theme: "image"
  };
}
function _temp2(fontSize_0) {
  return <option key={fontSize_0} className={fontSize_0} value={fontSize_0}>{fontSize_0}</option>;
}
function _temp3(textAlign_0) {
  return <option key={textAlign_0} value={textAlign_0}>{textAlign_0}</option>;
}